import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchThemeConfig,
  saveThemeConfig,
  type ThemeConfigResponse,
} from "service";
import {
  cloneDeep,
  DEFAULT_THEME_SLUG,
  getQueryCustomer,
  loadLocalThemes,
  sanitizeCustomer,
} from "containers/theme-setter/utils";
import {
  buildLandingFieldDefinitions,
} from "containers/theme-setter/definitions";
import { getTextVariantPreview } from "containers/theme-setter/field-previews";
import type {
  LandingFieldDefinition,
  TextVariantDisplay,
  TextVariantValue,
  ThemeConfig,
  ThemeContextInfo,
  ThemeLanding,
} from "containers/theme-setter/types";

type SaveStatus = "idle" | "saving" | "success";

type UseThemeSetterResult = {
  themeConfig: ThemeConfig | null;
  context?: ThemeContextInfo;
  normalizedCustomer: string;
  showPlaceholder: boolean;
  loading: boolean;
  saveError: string | null;
  saveStatus: SaveStatus;
  fetchError: string | null;
  isDirty: boolean;
  colors: Record<string, string>;
  fonts: NonNullable<ThemeConfig["fonts"]>;
  components: Record<string, string>;
  landingFieldDefinitions: LandingFieldDefinition[];
  textVariants: TextVariantDisplay[];
  handleColorChange: (token: string, value: string) => void;
  handleFontChange: (field: "body" | "heading", value: string) => void;
  handleComponentChange: (key: string, value: string) => void;
  handleLandingChange: (path: string, value: string) => void;
  handleTextVariantChange: (
    variant: string,
    field: "mobile" | "desktop",
    value: string
  ) => void;
  handleReset: () => void;
  handleSave: (overrideConfig?: ThemeConfig) => Promise<void>;
};

export const useThemeSetter = (): UseThemeSetterResult => {
  const [themeConfig, setThemeConfig] = useState<ThemeConfig | null>(null);
  const [loadedConfig, setLoadedConfig] = useState<ThemeConfig | null>(null);
  const [context, setContext] = useState<ThemeContextInfo | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

  const localThemes = useMemo(() => loadLocalThemes(), []);
  const requestedCustomer = useMemo(() => getQueryCustomer(), []);
  const normalizedCustomer = sanitizeCustomer(requestedCustomer);

  const getTemplateConfig = useCallback(
    (customer?: string): ThemeConfig | null => {
      const template =
        (customer && localThemes[customer]) || localThemes[DEFAULT_THEME_SLUG];
      if (!template) {
        return null;
      }
      return cloneDeep(template) as ThemeConfig;
    },
    [localThemes]
  );

  const setLoadedState = useCallback((config: ThemeConfig) => {
    const normalized = cloneDeep(config);
    setThemeConfig(normalized);
    setLoadedConfig(cloneDeep(normalized));
  }, []);

  const loadTheme = useCallback(async () => {
    if (!normalizedCustomer) return;
    setLoading(true);
    setFetchError(null);
    try {
      const response = await fetchThemeConfig(normalizedCustomer);
      const config = response.config as ThemeConfig;
      setLoadedState(config);
      setContext({
        resolvedCustomer: response.resolvedCustomer,
        source: response.source as ThemeConfigResponse["source"],
        exists: response.exists,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to load configuration.";
      setFetchError(message);
    } finally {
      setLoading(false);
    }
  }, [normalizedCustomer, setLoadedState]);

  useEffect(() => {
    if (!normalizedCustomer) {
      setThemeConfig(null);
      setLoadedConfig(null);
      setContext(undefined);
      return;
    }
    const template = getTemplateConfig(normalizedCustomer);
    if (template) {
      setLoadedState(template);
      setContext({
        resolvedCustomer: normalizedCustomer,
        source: localThemes[normalizedCustomer] ? "local" : "default",
        exists: Boolean(localThemes[normalizedCustomer]),
      });
    }
  }, [getTemplateConfig, localThemes, normalizedCustomer, setLoadedState]);

  useEffect(() => {
    if (!normalizedCustomer) return;
    loadTheme();
  }, [loadTheme, normalizedCustomer]);

  const showPlaceholder = !normalizedCustomer;

  const isDirty = useMemo(() => {
    if (!themeConfig || !loadedConfig) return false;
    return JSON.stringify(themeConfig) !== JSON.stringify(loadedConfig);
  }, [themeConfig, loadedConfig]);

  const updateThemeConfig = useCallback(
    (updater: (draft: ThemeConfig) => void) => {
      setThemeConfig((prev) => {
        if (!prev) return prev;
        const draft = cloneDeep(prev);
        updater(draft);
        return draft;
      });
    },
    []
  );

  const handleColorChange = useCallback(
    (token: string, value: string) => {
      updateThemeConfig((draft) => {
        draft.colors = draft.colors ?? {};
        draft.colors[token] = value;
      });
    },
    [updateThemeConfig]
  );

  const handleFontChange = useCallback(
    (field: "body" | "heading", value: string) => {
      updateThemeConfig((draft) => {
        draft.fonts = draft.fonts ?? {};
        draft.fonts[field] = value;
      });
    },
    [updateThemeConfig]
  );

  const handleComponentChange = useCallback(
    (key: string, value: string) => {
      updateThemeConfig((draft) => {
        draft.components = draft.components ?? {};
        draft.components[key] = value;
      });
    },
    [updateThemeConfig]
  );

  const handleLandingChange = useCallback(
    (path: string, value: string) => {
      const segments = path.split(".");
      updateThemeConfig((draft) => {
        draft.landing = draft.landing ?? ({} as ThemeLanding);
        let cursor: any = draft;
        segments.forEach((segment, index) => {
          if (index === segments.length - 1) {
            cursor[segment] = value;
            return;
          }
          if (!cursor[segment] || typeof cursor[segment] !== "object") {
            cursor[segment] = {};
          }
          cursor = cursor[segment];
        });
      });
    },
    [updateThemeConfig]
  );

  const handleTextVariantChange = useCallback(
    (variant: string, field: "mobile" | "desktop", value: string) => {
      updateThemeConfig((draft) => {
        draft.textVariants = draft.textVariants ?? {};
        const current = draft.textVariants[variant];
        let base: { mobile?: string; desktop?: string };
        if (!current) {
          base = {};
        } else if (typeof current === "string") {
          base = { mobile: current, desktop: current };
        } else {
          base = { ...current };
        }
        base[field] = value;
        draft.textVariants[variant] = base;
      });
    },
    [updateThemeConfig]
  );

  const handleReset = useCallback(() => {
    if (!loadedConfig) return;
    setThemeConfig(cloneDeep(loadedConfig));
  }, [loadedConfig]);

  const handleSave = useCallback(
    async (overrideConfig?: ThemeConfig) => {
      const configToSave = overrideConfig ?? themeConfig;
      if (!normalizedCustomer || !configToSave) return;
      setSaveError(null);
      setSaveStatus("saving");
      try {
        await saveThemeConfig(normalizedCustomer, configToSave);
        setThemeConfig(cloneDeep(configToSave));
        setLoadedConfig(cloneDeep(configToSave));
        setSaveStatus("success");
        await loadTheme();
      } catch (error) {
        setSaveStatus("idle");
        setSaveError(
          error instanceof Error
            ? error.message
            : "Unable to save configuration."
        );
      }
    },
    [loadTheme, normalizedCustomer, themeConfig]
  );

  const colors = (themeConfig?.colors ?? {}) as Record<string, string>;
  const components = (themeConfig?.components ?? {}) as Record<string, string>;
  const fonts = (themeConfig?.fonts ?? {}) as NonNullable<ThemeConfig["fonts"]>;

  const landingFieldDefinitions = useMemo(
    () => {
      const landingData = (themeConfig?.landing ?? {}) as ThemeLanding;
      return buildLandingFieldDefinitions(landingData);
    },
    [themeConfig?.landing]
  );

  const textVariantRows = useMemo<TextVariantDisplay[]>(
    () => {
      const variantData = (themeConfig?.textVariants ?? {}) as Record<
        string,
        TextVariantValue
      >;
      return Object.entries(variantData)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([variantKey, value]) => {
          const mobile = typeof value === "string" ? value : value?.mobile ?? "";
          const desktop =
            typeof value === "string" ? value : value?.desktop ?? "";
          return {
            key: variantKey,
            mobile,
            desktop,
            preview: getTextVariantPreview(variantKey),
          };
        });
    },
    [themeConfig?.textVariants]
  );

  return {
    themeConfig,
    context,
    normalizedCustomer,
    showPlaceholder,
    loading,
    saveError,
    saveStatus,
    fetchError,
    isDirty,
    colors,
    fonts,
    components,
    landingFieldDefinitions,
    textVariants: textVariantRows,
    handleColorChange,
    handleFontChange,
    handleComponentChange,
    handleLandingChange,
    handleTextVariantChange,
    handleReset,
    handleSave,
  };
};
